import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { clientsAPI, loyaltyAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Plus, Search, ChevronRight, Phone, MapPin, ShoppingBag, Archive, X,
} from 'lucide-react';

import { getErrorMessage } from '@/lib/errorUtils';
import { LoyaltyTierBadge } from '@/components/LoyaltyCard';
import { HelpTooltip } from '@/components/HelpTooltip';

const SERVICE_MODE_BADGE = {
  walk_in: { label: 'Walk-In', bg: 'rgba(30,122,95,0.12)', text: '#1E7A5F' },
  domestic_remote: { label: 'Domestic Remote', bg: 'rgba(37,99,235,0.10)', text: '#2563EB' },
  international: { label: 'International', bg: 'rgba(197,165,90,0.16)', text: '#C5A55A' },
};

export default function ClientsListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [total, setTotal] = useState(0);

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchQuery) params.q = searchQuery;
      if (serviceFilter !== 'all') params.service_mode = serviceFilter;
      const res = await clientsAPI.list(params);
      const data = res.data;
      setClients(data.clients || data || []);
      setTotal(data.total || (data.clients || data || []).length);
    } catch (err) { 
      toast.error(getErrorMessage(err, 'Failed to load clients')); 
    } finally { 
      setLoading(false); 
    }
  }, [searchQuery, serviceFilter]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === clients.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(clients.map(c => c.id)));
    }
  };

  const handleBulkArchive = async () => {
    setArchiving(true);
    try {
      const res = await clientsAPI.bulkArchive([...selectedIds]);
      toast.success(res.data.message || `${selectedIds.size} client(s) archived`);
      setSelectedIds(new Set());
      setArchiveDialogOpen(false);
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to archive');
    } finally {
      setArchiving(false);
    }
  };

  const selectedClients = clients.filter(c => selectedIds.has(c.id));
  const isAdmin = user?.role === 'Admin';

  return (
    <div className="space-y-5 pb-20">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--brand-navy)' }}>Clients</h1>
          <HelpTooltip text="Your client database. Click a client to see their profile, orders, and measurements." testId="help-clients" />
          <p className="text-sm mt-0.5" style={{ color: 'var(--ink-muted)' }}>{total} total</p>
        </div>
        <Button onClick={() => navigate('/app/clients/new')}
          className="h-10 rounded-xl px-4 text-sm" style={{ backgroundColor: 'var(--brand-navy)' }} data-testid="add-client-btn">
          <Plus className="w-4 h-4 mr-1" /> New Client
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
          <Input data-testid="client-search" placeholder="Search name, phone, email..." value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)} className="h-10 pl-9 rounded-xl" />
        </div>
        <Select value={serviceFilter} onValueChange={setServiceFilter}>
          <SelectTrigger className="h-10 w-[140px] rounded-xl text-sm" data-testid="service-filter">
            <SelectValue placeholder="All Modes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Modes</SelectItem>
            <SelectItem value="walk_in">Walk-In</SelectItem>
            <SelectItem value="domestic_remote">Domestic Remote</SelectItem>
            <SelectItem value="international">International</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Client List */}
      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}</div>
      ) : clients.length === 0 ? (
        <Card className="rounded-xl"><CardContent className="py-12 text-center">
          <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>No clients found</p>
        </CardContent></Card>
      ) : (
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="clients-table">
              <thead>
                <tr className="border-b" style={{ borderColor: 'rgba(27,42,74,0.08)' }}>
                  {isAdmin && (
                    <th className="py-2.5 px-3 w-10">
                      <Checkbox checked={selectedIds.size === clients.length && clients.length > 0}
                        onCheckedChange={toggleSelectAll} data-testid="select-all-checkbox" />
                    </th>
                  )}
                  <th className="text-left py-2.5 px-3 text-[10px] font-medium uppercase tracking-wider" style={{ color: 'var(--ink-muted)' }}>Client</th>
                  <th className="text-left py-2.5 px-3 text-[10px] font-medium uppercase tracking-wider hidden sm:table-cell" style={{ color: 'var(--ink-muted)' }}>Phone</th>
                  <th className="text-left py-2.5 px-3 text-[10px] font-medium uppercase tracking-wider hidden md:table-cell" style={{ color: 'var(--ink-muted)' }}>City</th>
                  <th className="text-left py-2.5 px-3 text-[10px] font-medium uppercase tracking-wider hidden lg:table-cell" style={{ color: 'var(--ink-muted)' }}>Mode</th>
                  <th className="text-right py-2.5 px-3 text-[10px] font-medium uppercase tracking-wider" style={{ color: 'var(--ink-muted)' }}>Orders</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {clients.map(c => {
                  const sm = SERVICE_MODE_BADGE[c.service_mode];
                  const isSelected = selectedIds.has(c.id);
                  return (
                    <tr key={c.id} className="border-b hover:bg-[color:var(--surface-2)] transition-colors cursor-pointer"
                      style={{ borderColor: 'rgba(27,42,74,0.04)', backgroundColor: isSelected ? 'rgba(197,165,90,0.06)' : undefined }}
                      onClick={() => navigate(`/app/clients/${c.id}`)}
                      data-testid={`client-row-${c.id}`}>
                      {isAdmin && (
                        <td className="py-2.5 px-3" onClick={e => e.stopPropagation()}>
                          <Checkbox checked={isSelected} onCheckedChange={() => toggleSelect(c.id)}
                            data-testid={`client-checkbox-${c.id}`} />
                        </td>
                      )}
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold" style={{ color: 'var(--navy-ink)' }}>
                            {c.first_name} {c.last_name}
                          </p>
                          {c.loyalty_tier && (
                            <LoyaltyTierBadge tier={c.loyalty_tier} />
                          )}
                        </div>
                      </td>
                      <td className="py-2.5 px-3 hidden sm:table-cell">
                        <span className="text-xs tabular-nums" style={{ color: 'var(--ink-muted)' }}>{c.phone}</span>
                      </td>
                      <td className="py-2.5 px-3 hidden md:table-cell">
                        <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>{c.location_city || '—'}</span>
                      </td>
                      <td className="py-2.5 px-3 hidden lg:table-cell">
                        {sm && <Badge variant="outline" className="text-[10px] px-1.5 py-0.5" style={{ backgroundColor: sm.bg, color: sm.text, border: 'none' }}>{sm.label}</Badge>}
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <span className="text-xs tabular-nums font-semibold" style={{ color: 'var(--navy-ink)' }}>
                          {c.order_count || 0}
                        </span>
                      </td>
                      <td className="py-2.5 px-2">
                        <ChevronRight className="w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Floating Action Bar */}
      {isAdmin && selectedIds.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-center p-3 pointer-events-none"
          data-testid="bulk-action-bar">
          <div className="flex items-center gap-3 px-5 py-3 rounded-2xl shadow-xl pointer-events-auto"
            style={{ backgroundColor: 'var(--brand-navy)', color: '#fff', maxWidth: '90vw' }}>
            <span className="text-sm font-semibold tabular-nums">{selectedIds.size} client{selectedIds.size > 1 ? 's' : ''} selected</span>
            <div className="w-px h-5" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }} />
            <Button size="sm" className="h-8 text-xs gap-1.5 rounded-lg"
              style={{ backgroundColor: '#DC2626' }}
              onClick={() => setArchiveDialogOpen(true)}
              data-testid="bulk-archive-btn">
              <Archive className="w-3.5 h-3.5" /> Archive Selected
            </Button>
            <Button variant="ghost" size="sm" className="h-8 text-xs gap-1 rounded-lg text-white/80 hover:text-white hover:bg-white/10"
              onClick={() => setSelectedIds(new Set())}
              data-testid="clear-selection-btn">
              <X className="w-3.5 h-3.5" /> Clear
            </Button>
          </div>
        </div>
      )}

      {/* Bulk Archive Confirmation Dialog */}
      <Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <DialogContent className="max-w-md" data-testid="bulk-archive-dialog">
          <DialogHeader>
            <DialogTitle style={{ color: '#DC2626' }}>Archive {selectedIds.size} Client{selectedIds.size > 1 ? 's' : ''}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>
              You are about to archive {selectedIds.size} client{selectedIds.size > 1 ? 's' : ''} and hide all their associated orders, production tasks, and payment records from active views. This action is logged.
            </p>
            <div className="max-h-32 overflow-y-auto space-y-1 p-2 rounded-lg" style={{ backgroundColor: 'rgba(27,42,74,0.03)' }}>
              {selectedClients.map(c => (
                <p key={c.id} className="text-xs font-medium" style={{ color: 'var(--navy-ink)' }}>
                  {c.first_name} {c.last_name}
                </p>
              ))}
            </div>
          </div>
          <DialogFooter className="gap-2 mt-2">
            <Button variant="outline" size="sm" onClick={() => setArchiveDialogOpen(false)}>Cancel</Button>
            <Button size="sm" disabled={archiving} style={{ backgroundColor: '#DC2626' }}
              onClick={handleBulkArchive} data-testid="confirm-bulk-archive-btn">
              {archiving ? 'Archiving...' : `Archive ${selectedIds.size} Client${selectedIds.size > 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
